"use client";
import { cn } from "@/lib/utils";
import { useMemo, type ReactNode } from "react";
import type { CampaignChannel, CampaignExample } from "../../data";
import { AdMock, EmailMock, SmsMock, WhatsAppMock } from "./components";
import { useCampaignStudio } from "./hooks";

interface CampaignStudioProps {
  campaigns: CampaignExample[];
  ariaLabel: string;
  tablistLabel: string;
  channelGroupLabel: string;
  allLabel: string;
  channelLabels: Record<CampaignChannel, string>;
  labels: {
    business: string;
    sentTo: string;
    trigger: string;
    businessAccount: string;
    textMessage: string;
    sponsored: string;
    time: string;
    smsTime: string;
    emailFooter: string;
  };
}

const channels: CampaignChannel[] = ["wa", "em", "sms", "ad"];

const factLabelClass =
  "text-[11.5px] font-extrabold tracking-[0.06em] text-ink-muted uppercase";

// The preview's `campaign_studio`: five campaign categories as tabs, each
// shown as a WhatsApp message, an email, an SMS and a digital ad. The channel
// switch narrows the grid to one mock-up.
const CampaignStudio = ({
  campaigns,
  ariaLabel,
  tablistLabel,
  channelGroupLabel,
  allLabel,
  channelLabels,
  labels,
}: CampaignStudioProps) => {
  const keys = useMemo(() => campaigns.map((campaign) => campaign.key), [campaigns]);
  const studio = useCampaignStudio(keys);
  const channelOptions = [
    { key: "all" as const, label: allLabel },
    ...channels.map((key) => ({ key, label: channelLabels[key] })),
  ];
  const isAll = studio.channel === "all";

  const renderMock = (campaign: CampaignExample, channel: CampaignChannel): ReactNode => {
    switch (channel) {
      case "wa":
        return (
          <WhatsAppMock
            campaign={campaign}
            subtitle={labels.businessAccount}
            time={labels.time}
          />
        );
      case "em":
        return <EmailMock campaign={campaign} footer={labels.emailFooter} />;
      case "sms":
        return (
          <SmsMock
            campaign={campaign}
            subtitle={labels.textMessage}
            time={labels.smsTime}
          />
        );
      case "ad":
        return <AdMock campaign={campaign} sponsored={labels.sponsored} />;
    }
  };

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      {/* In-page anchors (`#winback`), offset for the sticky header. */}
      {keys.map((key) => (
        <span key={key} id={key} className="relative -top-24 block h-0" />
      ))}

      <div className="mb-[22px] flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
        <div
          role="tablist"
          aria-label={tablistLabel}
          className="flex w-full gap-2 overflow-x-auto pb-1 sm:w-auto sm:flex-wrap sm:overflow-visible sm:pb-0"
        >
          {campaigns.map((campaign, index) => {
            const isActive = campaign.key === studio.active;
            return (
              <button
                key={campaign.key}
                type="button"
                role="tab"
                id={`cm-tab-${campaign.key}`}
                aria-controls={`cm-p-${campaign.key}`}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => studio.select(campaign.key)}
                onKeyDown={(event) => studio.onTabKeyDown(event, index)}
                className={cn(
                  "shrink-0 cursor-pointer rounded-full border-[1.5px] px-[18px] py-2.5 text-[15px] font-bold whitespace-nowrap transition-colors duration-200 motion-reduce:transition-none",
                  isActive
                    ? "border-primary bg-primary text-white"
                    : "border-line bg-white text-ink hover:border-primary",
                )}
              >
                {campaign.tab}
              </button>
            );
          })}
        </div>
        <div
          role="group"
          aria-label={channelGroupLabel}
          className="flex w-full gap-0.5 rounded-full bg-line p-1 sm:inline-flex sm:w-auto sm:flex-wrap"
        >
          {channelOptions.map((option) => {
            const isOn = option.key === studio.channel;
            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={isOn}
                onClick={() => studio.setChannel(option.key)}
                className={cn(
                  "flex-1 cursor-pointer rounded-full px-1 py-2 text-[13px] font-bold whitespace-nowrap sm:flex-none sm:px-3.5 sm:text-[14px]",
                  isOn ? "bg-white text-ink shadow-card" : "text-ink-muted hover:text-ink",
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {campaigns.map((campaign) => (
        <div
          key={campaign.key}
          role="tabpanel"
          id={`cm-p-${campaign.key}`}
          aria-labelledby={`cm-tab-${campaign.key}`}
          hidden={campaign.key !== studio.active}
        >
          <div className="mb-3.5 flex flex-wrap items-start justify-between gap-x-7 gap-y-[18px]">
            <div>
              <h3 className="mb-1.5 text-[1.6rem] leading-tight font-bold tracking-[-0.02em] text-ink">
                {campaign.title}
              </h3>
              <p className="max-w-[54ch] text-[16px] leading-normal text-ink-muted">
                {campaign.text}
              </p>
            </div>
            <div className="grid w-full gap-2 sm:w-auto sm:min-w-[420px] sm:grid-cols-2">
              <span className="grid gap-0.5 rounded-[14px] bg-white px-3.5 py-2.5">
                <small className={factLabelClass}>{labels.business}</small>
                <b className="text-[14.5px] leading-[1.35]">{campaign.business}</b>
              </span>
              <span className="grid gap-0.5 rounded-[14px] bg-white px-3.5 py-2.5">
                <small className={factLabelClass}>{labels.sentTo}</small>
                <b className="text-[14.5px] leading-[1.35]">{campaign.sentTo}</b>
              </span>
            </div>
          </div>

          {campaign.trigger && (
            <p className="mb-4 flex items-center gap-3 rounded-[14px] bg-tone-sun px-4 py-2.5 text-[15px] leading-snug text-tone-sun-ink">
              <b className="shrink-0 rounded-full bg-white px-2.5 py-[3px] text-[11.5px] tracking-[0.07em] uppercase">
                {labels.trigger}
              </b>
              <span>{campaign.trigger}</span>
            </p>
          )}

          <div
            className={cn(
              "grid items-start gap-5",
              isAll
                ? "sm:grid-cols-2 xl:grid-cols-4"
                : "grid-cols-[minmax(0,340px)] justify-center",
            )}
          >
            {channels.map((channel) => (
              <div
                key={channel}
                hidden={!isAll && channel !== studio.channel}
                className="grid min-w-0 justify-items-center gap-2.5"
              >
                <span className="rounded-full bg-white px-3 py-1 text-[12px] font-extrabold tracking-[0.08em] text-ink uppercase">
                  {channelLabels[channel]}
                </span>
                {renderMock(campaign, channel)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </figure>
  );
};

export default CampaignStudio;
